import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { motion } from 'framer-motion';

interface TeamMetricsProps {
  metrics?: {
    totalTeamMembers?: number;
    participationRate?: number;
    pending?: number;
    responseRate?: number;
    submitted?: number;
    reviewed?: number;
  };
}

const TeamMetrics: React.FC<TeamMetricsProps> = ({ metrics }) => {
  const { t } = useTranslation();

  const metricCards = [
    {
      label: t('performance.managerResponse.teamMembers'),
      value: metrics?.totalTeamMembers?.toString() ?? '0',
      icon: 'Users' as const,
      color: 'from-blue-500 to-cyan-500',
      description: t('performance.managerResponse.totalTeamSize')
    },
    {
      label: t('performance.managerResponse.participationRate'),
      value: `${metrics?.participationRate ?? 0}%`,
      icon: 'TrendingUp' as const,
      color: 'from-green-500 to-emerald-500',
      description: t('performance.managerResponse.submittedCount', { count: metrics?.submitted ?? 0 })
    },
    {
      label: t('performance.managerResponse.pendingReview'),
      value: metrics?.pending?.toString() ?? '0',
      icon: 'Clock' as const,
      color: 'from-orange-500 to-amber-500',
      description: t('performance.managerResponse.awaitingResponse')
    },
    {
      label: t('performance.managerResponse.responseRate'),
      value: `${metrics?.responseRate ?? 0}%`,
      icon: 'CheckCircle' as const,
      color: 'from-pink-500 to-rose-500',
      description: t('performance.managerResponse.respondedCount', { count: metrics?.reviewed ?? 0 })
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {metricCards.map((metric, index) => (
        <motion.div
          key={metric.label}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: index * 0.1 }}
          className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-1">{metric.label}</p>
              <p className="text-2xl font-bold text-foreground mb-1">{metric.value}</p>
              <p className="text-xs text-muted-foreground">{metric.description}</p>
            </div>
            <div className={`w-10 h-10 bg-gradient-to-br ${metric.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <Icon name={metric.icon} size={20} color="white" strokeWidth={2} />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default TeamMetrics;